import React, { useState } from 'react';
import Modal from 'react-modal';
import styles from './AddRefundModal.module.css';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { toast } from 'react-toastify';
import updateNumberValue from '../util/updateNumberValue';
import ConfirmationModal from './ConfirmationModal';
import { useAuth } from '../hooks/useAuth';

interface AddCreditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  customerId: number;
  customerName: string;
}

const AddCreditModal: React.FC<AddCreditModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  customerId,
  customerName,
}) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [amount, setAmount] = useState('0.00');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<{ amount?: string; notes?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNotificationConfirm, setShowNotificationConfirm] = useState(false);

  const resetFields = () => {
    setAmount('0.00');
    setNotes('');
    setErrors({});
  };

  const isNegative = Number(amount) < 0;

  const validate = () => {
    const errs: { amount?: string; notes?: string } = {};
    if (!amount || Number(amount) === 0) errs.amount = 'Amount must not be zero';
    if (isNegative && !isAdmin) errs.amount = 'Only admins can remove credit';
    if (!notes.trim()) errs.notes = 'Notes are required';
    return errs;
  };

  const handleSubmit = () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    if (isNegative) {
      submitCredit(false);
    } else {
      setShowNotificationConfirm(true);
    }
  };

  const submitCredit = async (sendNotification: boolean) => {
    setShowNotificationConfirm(false);
    setIsSubmitting(true);
    try {
      const res = await fetchWithAuth(`/customers/${customerId}/credits`, {
        method: 'POST',
        body: JSON.stringify({ amount, notes, send_notification: sendNotification }),
      });

      if (res.ok) {
        toast.success(Number(amount) < 0 ? 'Credit removed successfully' : 'Credit added successfully');
        resetFields();
        onSuccess();
      } else {
        const data = await res.json();
        toast.error(data.errors?.[0] || 'Failed to add credit');
      }
    } catch {
      toast.error('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    resetFields();
    onClose();
  };

  return (
    <>
      <Modal isOpen={isOpen} onRequestClose={handleClose} className={styles.modalContent}>
        <h2 className="text-lg font-semibold mb-4">{isNegative ? 'Remove Credit' : 'Add Credit'} — {customerName}</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Amount</label>
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={e => updateNumberValue(e.target.value, setAmount)}
              className="w-full border rounded px-3 py-2 mt-1"
            />
            {errors.amount && <p className="text-red-500 text-sm mt-1">{errors.amount}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Notes</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="e.g. Giving credit as a goodwill gesture for the late delivery"
              className="w-full border rounded px-3 py-2 mt-1"
              rows={3}
            />
            {errors.notes && <p className="text-red-500 text-sm mt-1">{errors.notes}</p>}
          </div>
          <div className="flex justify-end space-x-2 mt-4">
            <button
              className="bg-gray-300 px-4 py-2 rounded hover:bg-gray-400"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800 disabled:opacity-50"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isNegative ? 'Remove Credit' : 'Add Credit'}
            </button>
          </div>
        </div>
      </Modal>

      {showNotificationConfirm && (
        <ConfirmationModal
          isOpen={showNotificationConfirm}
          message="Would you like to send a WhatsApp notification to the customer?"
          onConfirm={() => submitCredit(true)}
          onCancel={() => submitCredit(false)}
        />
      )}
    </>
  );
};

export default AddCreditModal;
